import { isNil, pick } from 'lodash'
import { omit } from 'lodash'
import { IntlShape } from 'react-intl'

import {
  Client,
  ClientVersion,
  CreateGeneralInsuranceProduct,
  GeneralInsuranceQuoteStage,
  Maybe,
  omitTypename,
  PropertyVersion,
} from '@acre/graphql'

enum PROPERTY_MAPPING {
  title = 'title',
  first_name = 'first name',
  last_name = 'last name',
  date_of_birth = 'date of birth',
  number_of_bedrooms = 'number of bedrooms',
  year_property_built = 'year property built',
  address1 = 'first line of address',
  posttown = 'city',
  postcode = 'postcode ',
  country = 'country',
}

const clientErrorFilter = ['title', 'first_name', 'last_name', 'date_of_birth']
const propertyFilter = ['number_of_bedrooms', 'year_property_built']
const propertyAddressFilter = ['address1', 'posttown', 'postcode', 'country']
const messagePrefix = 'generalInsurance'

// Generate an array of errors to display in the error message.
const generateErrors = (object: Object) => {
  let errors: (string | undefined)[] = []
  for (const [key, value] of Object.entries(object)) {
    if (isNil(value)) {
      errors.push(key)
    }
  }
  return errors
}

export const postRecommendationGIProductStages = [
  GeneralInsuranceQuoteStage.Selected,
  GeneralInsuranceQuoteStage.ApplicationInProgress,
  GeneralInsuranceQuoteStage.Current,
]

export const isSourcedGIProduct = (giProduct: Maybe<CreateGeneralInsuranceProduct>) => {
  return giProduct?.details?.provider !== 'MANUAL'
}

// Here we check every property in the object we want to send to GI
const checkObjectProperties = (object: Partial<PropertyVersion | Client>, intl: IntlShape) => {
  const formatMessage = (id: string, value?: string) => intl.formatMessage({ id }, { value })

  let erroringClientFields: (string | undefined)[] = []
  let erroringPropertyFields: (string | undefined)[] = []

  const clientObject = generateErrors(pick(object, clientErrorFilter))
  const property = generateErrors(pick(object, 'property'))
  const propertyObject = generateErrors(pick(object, propertyFilter))
  const propertyAddressObject = generateErrors(pick(object, propertyAddressFilter))

  erroringClientFields = [...clientObject]
  erroringPropertyFields = [...property, ...propertyObject, ...propertyAddressObject]

  if (erroringClientFields.length !== 0 || erroringPropertyFields.length !== 0) {
    const filterPropertyIfEmpty = erroringPropertyFields.filter((item) => item !== 'property')

    //The following two maps, are to remove any underscore and join with a comma.
    const formattedPropertyFields = filterPropertyIfEmpty
      .map((value) => value && PROPERTY_MAPPING[value as keyof typeof PROPERTY_MAPPING])
      .join(', ')

    const formattedPropertyFieldsMessage = erroringPropertyFields.includes('property')
      ? 'property details'
      : formattedPropertyFields
        ? `property ${formattedPropertyFields}`
        : ''

    const formattedPropertyFieldsErrorMessage = formatMessage(
      `${messagePrefix}.propertyDetailsMissing`,
      formattedPropertyFieldsMessage,
    )

    const formattedClientFields = erroringClientFields
      .map((value) => value && PROPERTY_MAPPING[value as keyof typeof PROPERTY_MAPPING])
      .join(', ')

    const formattedClientFieldsMessage = formattedClientFields
      ? `client ${formattedClientFields} to retrieve quotes.`
      : ''

    const formattedClientFieldsErrorMessage = formatMessage(
      `${messagePrefix}.clientDetailsMissing`,
      formattedClientFieldsMessage,
    )

    const errorMessage = () => {
      const error = `${formattedPropertyFieldsErrorMessage
        .concat(formattedClientFields && formattedPropertyFields && formattedClientFieldsErrorMessage ? ' and ' : '')
        .concat((formattedClientFields && formattedClientFieldsErrorMessage) || '')}`
      return error.trim()
    }

    throw new Error(errorMessage())
  }
}

// client structure required for request:
// clients: [
//     {
//       client_id: '30b52d99-e206-495b-99bd-11d8d1f4ea96',
//       details: {
//         date_of_birth: '1980-01-02',
//         first_name: 'Albertha',
//         last_name: 'Boyer',
//         title: 'Ms'
//       }
//     }
//   ]

// property structure required for request:
// property: {
//     details: {
//       address: {
//         address1: 'Yut Mobiles',
//         address2: '518 Stratford Road',
//         address3: 'Sparkhill',
//         country: 'GB',
//         county: 'West Midlands',
//         postcode: 'B11 4AJ',
//         posttown: 'Birmingham'
//       },
//       number_of_bedrooms: 2,
//       property_type: 'MAISONETTE',
//       year_property_built: 1990
//     },
//     property_id: '99de3d0d-c28d-4eac-9e06-18f7d55a23ba'
//   }

export const pickGeneralInsuranceClientData = (
  clients: ClientVersion[],
  property: PropertyVersion | null | undefined,
  intl: IntlShape,
) => {
  try {
    const filteredProperty = property
      ? { ...pick(property?.details.address, propertyAddressFilter), ...pick(property?.details, propertyFilter) }
      : { property: null }

    const formattedClients = {
      clients: clients.map((client) => {
        const filteredClient = pick(client.details, clientErrorFilter)
        const clientData = { ...filteredProperty, ...filteredClient }
        checkObjectProperties(omit(clientData, '__typename'), intl)

        const title =
          client.details.title && client.details.title.charAt(0) + client.details.title?.slice(1).toLowerCase()

        return {
          client_id: client.id,
          details: {
            title: title,
            first_name: client.details.first_name,
            last_name: client.details.last_name,
            date_of_birth: client.details.date_of_birth,
          },
        }
      }),
    }

    const formattedProperty = {
      property: {
        details: {
          address: { address2: null, address3: null, ...pick(property?.details.address, propertyAddressFilter) },
          ...pick(property?.details, propertyFilter),
          property_type: property?.details.type,
        },
        property_id: property?.id,
      },
    }

    return { ...omitTypename(formattedClients), ...omitTypename(formattedProperty) }
  } catch (e) {
    return (e as Error).message
  }
}
